import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import ClientLayout from '../../components/layout/ClientLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import {
  ClipboardList, CalendarDays, Users, UserCheck, UserX,
  CheckCircle2, XCircle, Plus, Building2, Mail, Phone,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STAT_TILES = [
  { key: 'active_requests', label: 'Active Staffing Requests', icon: ClipboardList },
  { key: 'upcoming_jobs', label: 'Upcoming Jobs', icon: CalendarDays },
  { key: 'staff_requested', label: 'Staff Requested', icon: Users },
  { key: 'staff_confirmed', label: 'Staff Confirmed', icon: UserCheck },
  { key: 'remaining_vacancies', label: 'Remaining Vacancies', icon: UserX },
  { key: 'completed_jobs', label: 'Completed Jobs', icon: CheckCircle2 },
  { key: 'cancelled_jobs', label: 'Cancelled Jobs', icon: XCircle },
];

export default function ClientDashboardPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchDashboard = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/client/portal/dashboard`);
      setData(res.data);
    } catch (e) {
      console.error('Failed to load client dashboard:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchDashboard(); }, [fetchDashboard]);

  return (
    <ClientLayout>
      <div className="space-y-4 sm:space-y-6" data-testid="client-dashboard">
        {/* Welcome */}
        <div>
          <h1 className="text-xl sm:text-3xl font-bold text-zinc-900 tracking-tight">
            Welcome{data?.contact_name ? `, ${data.contact_name}` : ''}
          </h1>
          <p className="text-sm text-zinc-500 mt-0.5">
            Your staffing overview at a glance.
          </p>
        </div>

        {/* Company card */}
        <Card className="bg-white border-zinc-200 rounded-lg shadow-sm">
          <CardContent className="p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-6">
            <div className="flex items-center gap-3">
              <span className="w-11 h-11 rounded-full bg-[#14181f] flex items-center justify-center shrink-0">
                <Building2 className="w-5 h-5 text-primary" strokeWidth={1.75} />
              </span>
              <div>
                <p className="text-sm sm:text-base font-bold text-zinc-900" data-testid="client-company-name">
                  {data?.company_name || (loading ? 'Loading…' : user?.name)}
                </p>
                <p className="text-xs text-zinc-500">{data?.contact_name}</p>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-1.5 sm:gap-6 sm:ml-auto text-xs sm:text-sm text-zinc-600">
              {data?.email && (
                <span className="flex items-center gap-1.5"><Mail className="w-3.5 h-3.5 text-zinc-400" />{data.email}</span>
              )}
              {data?.phone && (
                <span className="flex items-center gap-1.5"><Phone className="w-3.5 h-3.5 text-zinc-400" />{data.phone}</span>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Create request CTA — enabled in Phase 2 */}
        <Card className="bg-[#14181f] border-0 rounded-lg">
          <CardContent className="p-4 sm:p-6 flex flex-col sm:flex-row items-start sm:items-center gap-3">
            <div className="flex-1">
              <p className="text-white font-bold text-sm sm:text-lg">Need staff for an upcoming job?</p>
              <p className="text-white/60 text-xs sm:text-sm mt-0.5">
                Request Labour, Security or Retail staff — jobs are created instantly and our team is notified.
              </p>
            </div>
            <Button
              onClick={() => navigate('/client/requests/new')}
              data-testid="create-staffing-request-btn"
              className="bg-primary text-black hover:bg-yellow-300 rounded-md uppercase tracking-[0.12em] text-xs font-semibold h-10 px-4"
            >
              <Plus className="w-4 h-4 mr-1.5" />
              Create Staffing Request
            </Button>
          </CardContent>
        </Card>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2.5 sm:gap-4">
          {STAT_TILES.map((t) => (
            <Card key={t.key} className="bg-white border-zinc-200 rounded-lg shadow-sm" data-testid={`client-stat-${t.key}`}>
              <CardContent className="p-3 sm:p-4">
                <div className="flex items-center justify-between">
                  <t.icon className="w-4 h-4 sm:w-5 sm:h-5 text-primary" strokeWidth={1.75} />
                  <span className="text-xl sm:text-2xl font-bold text-zinc-900">
                    {loading ? '—' : (data?.stats?.[t.key] ?? 0)}
                  </span>
                </div>
                <p className="mt-1.5 text-[10px] sm:text-xs text-zinc-500 uppercase tracking-wider leading-tight">{t.label}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </ClientLayout>
  );
}
